import { Box, LinearProgress, Skeleton, Typography } from '@mui/material'
import { ReactElement } from 'react'
import type { IBSCProgressData } from '../ibsc.slice'

type Props = {
  data?: IBSCProgressData
  isLoading: boolean
}

type ProgramStatProps = {
  label: string
  totalHours: number
  requiredHours: number
  completionPercent: number
  color: 'success' | 'warning'
  labelColor: string
}

let ProgramStat = (props: ProgramStatProps): ReactElement => {
  let {
    label,
    totalHours,
    requiredHours,
    completionPercent,
    color,
    labelColor,
  } = props

  return (
    <Box sx={{ textAlign: 'center', minWidth: 140 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: labelColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{ color: labelColor, fontWeight: 600 }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {totalHours.toFixed(1)}/{requiredHours} hrs
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 1,
        }}
      >
        {completionPercent}% Complete
      </Typography>
      <LinearProgress
        variant="determinate"
        value={completionPercent}
        sx={{
          color,
          height: 6,
          borderRadius: 3,
          bgcolor: 'action.disabledBackground',
        }}
      />
    </Box>
  )
}

export default (props: Props): ReactElement => {
  let { data, isLoading } = props

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 6,
          pt: 2,
          flexWrap: 'wrap',
        }}
      >
        <Skeleton
          variant="rectangular"
          sx={{
            width: 140,
            height: 80,
            borderRadius: 1,
          }}
        />
        <Skeleton
          variant="rectangular"
          sx={{
            width: 140,
            height: 80,
            borderRadius: 1,
          }}
        />
      </Box>
    )
  }

  if (!data) return <></>

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 6,
        pt: 2,
        flexWrap: 'wrap',
      }}
    >
      <ProgramStat
        label="IBSC 40"
        totalHours={data.ibsc40.totalHours}
        requiredHours={data.ibsc40.requiredHours}
        completionPercent={data.ibsc40.completionPercent}
        labelColor="success.main"
        color="success"
      />
      <ProgramStat
        label="IBSC 100"
        totalHours={data.ibsc100.totalHours}
        requiredHours={data.ibsc100.requiredHours}
        completionPercent={data.ibsc100.completionPercent}
        labelColor="warning.main"
        color="warning"
      />
    </Box>
  )
}
